import type { RecentMainCommandChat } from './main-command-chat-archive';
import type { MainCommandReplyTarget } from './main-command-recipients';
import type { SharedTimelineItem } from './shared-command-timeline';

export const MINI_MAIN_COMMAND_MAX_TURNS = 8;

/**
 * Final host wiring after merged #531 and the merged #510 repair:
 *
 * - OperationsRoom remains the sole owner of the canonical management target set,
 *   WorkerChatReadiness participants, selected Worker ids, Management Attention
 *   count, shared timeline, draft and guarded send callback.
 * - Lift CommandChat's active current/archive selection so the full and floating
 *   presentations receive the same RecentMainCommandChat and selected timeline.
 * - Opening Management Attention routes to the existing #main-command-attention
 *   surface; opening full Command switches to Control without changing selection.
 * - Mount the floating presentation only when section !== 'control'.
 * - Do not add Supervisor/specialist chips here. #530 routes their reports upward;
 *   only the canonical management targets and explicitly selected Workers are
 *   normal Main Command recipients.
 */

/** Resolve the exact conversation already selected by the canonical #493 organizer. */
export function activeMiniConversation(
  items: readonly RecentMainCommandChat[],
  selectedArchiveId: string | null,
): RecentMainCommandChat | null {
  if (selectedArchiveId === null) {
    return items.find((item) => item.kind === 'current') ?? null;
  }
  return items.find((item) => item.kind === 'archived' && item.id === selectedArchiveId) ?? null;
}

/** A bounded tail of the already-selected canonical shared timeline; never another transcript/store. */
export function boundedMiniTimeline(
  items: readonly SharedTimelineItem[],
  limit: number = MINI_MAIN_COMMAND_MAX_TURNS,
): readonly SharedTimelineItem[] {
  const safeLimit = Number.isFinite(limit) ? Math.max(1, Math.floor(limit)) : MINI_MAIN_COMMAND_MAX_TURNS;
  return items.slice(Math.max(0, items.length - safeLimit));
}

export function miniConversationStateLabel(conversation: RecentMainCommandChat | null): string {
  if (conversation === null) return 'Main Command';
  return conversation.kind === 'current' ? 'Current conversation' : 'Archived conversation · read only';
}

/**
 * The floating presentation shares the full composer's send authority. Management
 * targets and Worker ids stay in their canonical #531 shapes; this adapter does not
 * invent a combined recipient identity or another selection store.
 */
export function miniCanSend(input: {
  readonly activeConversation: RecentMainCommandChat | null;
  readonly selectedParticipants: ReadonlySet<MainCommandReplyTarget>;
  readonly selectedWorkers: ReadonlySet<string>;
  readonly draft: string;
  readonly sending: boolean;
}): boolean {
  if (input.activeConversation?.kind === 'archived') return false;
  if (input.sending || input.draft.trim() === '') return false;
  return input.selectedParticipants.size > 0 || input.selectedWorkers.size > 0;
}
